import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContractsTable, {
  type ContractsTableColumn,
} from '@/modules/org/contracts/components/contracts-table';
import CommonContractHeader from '@/modules/org/contracts/components/common-contracts-header';
import DocumentStatusBadge from '@/modules/org/contracts/components/document-status-badge';
import { useContractArchiveActions } from '@/modules/org/contracts/hooks/use-contract-archive-actions';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { useGetOrgContractDocuments } from '@/graphql';
import { OrgContractDocumentItem } from '../../contracts/types/contract-document.types';
import {
  ContractDocumentRow,
  mapItemToRow,
  PAGE_SIZE,
} from '../../contracts/constants/mock-contract-documents';
import TrashRowActions from '../components/trash-row-actions';
import TrashDataCardView from '../components/trash-data-card-view';
import { useDebounce } from '@/hooks/use-debounce';

const OrgTrashPage = () => {
  const { t } = useTranslation();
  const orgId = useCurrentOrgId();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(
    () => ({
      paging: { pageNo: currentPage, pageSize: PAGE_SIZE },
      where: {
        IsInTrash: { eq: true },
        ...(orgId ? { OrganizationId: { eq: orgId } } : {}),
        ...(debouncedSearch.trim()
          ? { TemplateTitle: { contains: debouncedSearch.trim() } }
          : {}),
      },
    }),
    [orgId, debouncedSearch, currentPage]
  );

  const { data: documentsResult, isLoading: isDocumentsLoading } =
    useGetOrgContractDocuments(queryParams);
  const data = documentsResult?.getOrgContractDocuments;
  const { isUpdating, updatingItemId, restoreFromTrashDocument } = useContractArchiveActions();

  const documents = useMemo(() => {
    if (!data?.items?.length) return [];

    return data.items.map((item) => mapItemToRow(item as OrgContractDocumentItem));
  }, [data]);

  const handleRestore = async (row: ContractDocumentRow) => {
    await restoreFromTrashDocument(row.id);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const total = data?.totalCount || 0;
  const totalPages = data?.totalCount ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;

  const startItem = total === 0 ? 0 : startIndex + 1;
  const endItem = total === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, total);

  const columns: ContractsTableColumn<ContractDocumentRow>[] = [
    {
      id: 'title',
      header: t('ORGCONTRACTS_COLUMNS_TITLE'),
      cell: (row) => (
        <div className="space-y-1">
          <p className="max-w-90 truncate text-[15px] font-semibold leading-5 text-[hsl(var(--fg))]">
            {row.title}
          </p>
          <p className="text-sm text-globe-grey">{row.recipientsSummary ?? '-'}</p>
        </div>
      ),
    },
    {
      id: 'deletedAt',
      header: t('ORGCONTRACTS_COLUMNS_DELETEDAT'),
      cell: (row) => (
        <p className="text-[15px] font-semibold text-[hsl(var(--fg))]">{row.lastEditedAt}</p>
      ),
    },
    {
      id: 'status',
      header: t('ORGCONTRACTS_COLUMNS_STATUS'),
      cell: (row) => <DocumentStatusBadge status={row.status} />,
    },
    {
      id: 'action',
      header: t('ORGCONTRACTS_COLUMNS_ACTION'),
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => (
        <TrashRowActions
          isLoading={isUpdating && updatingItemId === row.id}
          handleRestore={() => handleRestore(row)}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6 bg-white font-nunito">
      <CommonContractHeader
        title="ORGTRASH_HEADER_TITLE"
        subtitle="ORGTRASH_HEADER_SUBTITLE"
        search={search}
        setSearch={setSearch}
        searchPlaceholder="ORGCONTRACTS_SEARCHPLACEHOLDER"
      />

      {/* Desktop: classic data table */}
      <div className="hidden md:block">
        <ContractsTable
          columns={columns}
          data={documents}
          rowKey={(row) => row.id}
          emptyMessage={t('ORGTRASH_EMPTY')}
          isLoading={isDocumentsLoading}
          loadingRowCount={PAGE_SIZE}
          pagination={{
            page: safePage,
            limit: PAGE_SIZE,
            total,
            totalPages,
          }}
          onPageChange={(page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages))}
          showingLabel={t('ORGTRASH_SHOWING_RANGE', {
            start: String(startItem).padStart(2, '0'),
            end: String(endItem).padStart(2, '0'),
            total,
          })}
        />
      </div>

      {/* Mobile: card layout sharing the same data + handlers */}
      <div className="md:hidden">
        <TrashDataCardView
          documents={documents}
          isLoading={isDocumentsLoading}
          loadingRowCount={PAGE_SIZE}
          emptyMessage={t('ORGTRASH_EMPTY')}
          isUpdating={isUpdating}
          updatingItemId={updatingItemId}
          onRestore={handleRestore}
          pagination={{
            page: safePage,
            limit: PAGE_SIZE,
            total,
            totalPages,
          }}
          onPageChange={(page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages))}
          showingLabel={t('ORGTRASH_SHOWING_RANGE', {
            start: String(startItem).padStart(2, '0'),
            end: String(endItem).padStart(2, '0'),
            total,
          })}
        />
      </div>
    </div>
  );
};

export default OrgTrashPage;
